import { getApiBaseUrl } from '@/core/utils/apiUtils';
import { getCookie } from '@/core/utils/cookieUtils';
import {
  getCommonServerAttributes,
  getPageName,
  getUserInfo,
} from '@/shared/features/analytics/utils/commonAttributes';
import type { ProductDetail } from '@/features/pdp/domain/models/ProductDetail';
import type { PdpEffectiveProductVm } from '@/features/pdp/presentation/viewModels/pdp.vm';
import { formatViewContentPrice } from '@/features/pdp/presentation/analytics/pdpViewContent';
import { parseSizeList } from '@/features/pdp/utils/pdpCustomization';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _connecto?: { getAnonymousId?: () => string };
  }
}

export type FacebookServerLogger = (payload: Record<string, unknown>) => void;

let facebookServerLogger: FacebookServerLogger | null = null;

/** Register eventLogger callback from analytics init (optional; falls back to direct fetch). */
export function setFacebookServerLogger(logger: FacebookServerLogger | null): void {
  facebookServerLogger = logger;
}

function getCurrentPageType(): string {
  const pageNameAttr = getPageName();
  return pageNameAttr['Page Name'] || '';
}

export function generateEventId(): string {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export function removeEmptyParams(
  params: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    }),
  );
}

/** Meta `content_ids` / `content_names` must always be `string[]`, including a single value. */
export function toFacebookStringArray(value: unknown): string[] {
  if (value == null || value === '') return [];
  const list = Array.isArray(value) ? value : [value];
  return list
    .filter((item) => item != null && item !== '')
    .map(String);
}

/** @deprecated Use `toFacebookStringArray`. */
export function toFacebookContentIds(value: unknown): string[] {
  return toFacebookStringArray(value);
}

function withNormalizedContentArrays(
  eventAttributes: Record<string, unknown>,
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...eventAttributes };

  if ('content_ids' in next) {
    next.content_ids = toFacebookStringArray(next.content_ids);
  }

  if ('content_names' in next || 'content_name' in next) {
    next.content_names = toFacebookStringArray(next.content_names ?? next.content_name);
    delete next.content_name;
  }

  return next;
}

export function normalizePhoneForFacebookEvents(phone: unknown): string | null {
  if (!phone) return null;
  let normalized = String(phone).trim();
  normalized = normalized.replace(/[+\-\s()]/g, '');
  if (/^\d{10}$/.test(normalized)) {
    normalized = `91${normalized}`;
  }
  return normalized || null;
}

export function getFirstNameForFacebook(userInfo: ReturnType<typeof getUserInfo>): string | null {
  if (!userInfo) return null;
  const first =
    typeof userInfo.firstName === 'string' && userInfo.firstName.trim()
      ? userInfo.firstName.trim()
      : '';
  const middle =
    typeof userInfo.middleName === 'string' && userInfo.middleName.trim()
      ? ` ${userInfo.middleName.trim()}`
      : '';
  const last =
    typeof userInfo.lastName === 'string' && userInfo.lastName.trim()
      ? userInfo.lastName.trim()
      : '';
  const fullName =
    (typeof userInfo.customerName === 'string' && userInfo.customerName.trim()) ||
    (typeof userInfo.userName === 'string' && userInfo.userName.trim()) ||
    '';

  // user-context cache used to only keep customerName; never send that as fn.
  let firstName = first;
  if (firstName && fullName && firstName === fullName) {
    firstName =
      last && firstName.toLowerCase().endsWith(` ${last.toLowerCase()}`)
        ? firstName.slice(0, firstName.length - last.length).trim()
        : '';
  }

  return (firstName + middle).trim() || null;
}

function getDeliveryCity(userInfo: ReturnType<typeof getUserInfo>): string | null {
  if (userInfo?.deliveryCity && typeof userInfo.deliveryCity === 'string') {
    return userInfo.deliveryCity.trim() || null;
  }
  if (userInfo?.cityName && typeof userInfo.cityName === 'string') {
    return userInfo.cityName.trim() || null;
  }
  return null;
}

export function getConnectoId(): string {
  if (typeof window === 'undefined') return '';
  try {
    return window._connecto?.getAnonymousId?.() ?? '';
  } catch {
    return '';
  }
}

export function getFacebookCommonAttributes(
  customAttributes?: Record<string, unknown>,
): Record<string, unknown> {
  if (typeof window === 'undefined') {
    return { ...customAttributes };
  }

  const userInfo = getUserInfo();
  const isLoggedIn = userInfo?.isLoggedIn === true;

  const external_id =
    isLoggedIn && typeof userInfo?.userCode === 'string' && userInfo.userCode.trim()
      ? userInfo.userCode.trim()
      : null;
  const em =
    isLoggedIn && typeof userInfo?.email === 'string'
      ? userInfo.email.trim().toLowerCase()
      : null;
  const ph =
    isLoggedIn && userInfo?.phoneNumber
      ? normalizePhoneForFacebookEvents(userInfo.phoneNumber)
      : null;

  const fn = isLoggedIn ? getFirstNameForFacebook(userInfo) : null;
  const ln =
    isLoggedIn && typeof userInfo?.lastName === 'string' && userInfo.lastName.trim()
      ? userInfo.lastName.trim()
      : null;

  const ct = isLoggedIn && typeof userInfo?.cityName === 'string' ? userInfo.cityName : null;
  const st = isLoggedIn && typeof userInfo?.state === 'string' ? userInfo.state : null;
  const zp =
    userInfo?.guessedPincode === true || !userInfo?.pincode
      ? null
      : String(userInfo.pincode);
  // Meta CAPI `country` must be ISO 3166-1 alpha-2 (lowercase). Always India.
  const country = 'in';

  const commonAttributes: Record<string, unknown> = {
    event_time: Math.floor(Date.now() / 1000),
    event_source_url: window.location.href,
    fbp: getCookie('_fbp'),
    fbc: getCookie('_fbc'),
    external_id,
    em,
    ph,
    fn,
    ln,
    ct,
    st,
    zp,
    country,
    currency: 'INR',
    source_surface: 'website',
    producer_system: 'web_pixel',
    surface_identifier: window.location.pathname,
    surface_title: document.title,
    surface_type: getCurrentPageType(),
    city: getDeliveryCity(userInfo),
  };

  return {
    ...commonAttributes,
    ...customAttributes,
  };
}

function resolveEventLoggerBaseUrl(): string | null {
  const explicit = import.meta.env.VITE_EVENT_LOGGER_URL?.trim();
  if (explicit) return explicit.replace(/\/$/, '');

  const pageApi = import.meta.env.VITE_PAGE_API_BASE_URL?.trim();
  if (pageApi) return pageApi.replace(/\/page\/?$/, '');

  const apiBase = getApiBaseUrl();
  if (apiBase) return apiBase.replace(/\/$/, '');

  return null;
}

function toFormValue(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}

function toFormBody(payload: Record<string, unknown>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(payload)) {
    if (value === undefined || value === null) continue;
    params.append(key, toFormValue(value));
  }
  return params.toString();
}

function logFacebookServerEvent(eventName: string, eventAttributes: Record<string, unknown>): void {
  const { contents, ...restAttributes } = eventAttributes;
  const payload = removeEmptyParams({
    _eventId: eventName,
    metaEvent: true,
    ...getFacebookCommonAttributes(),
    client_user_agent: navigator.userAgent,
    ...restAttributes,
    ...(typeof contents === 'string'
      ? { contents }
      : contents != null && !(Array.isArray(contents) && contents.length === 0)
        ? { contents: toFormValue(contents) }
        : null),
  });

  if (facebookServerLogger) {
    facebookServerLogger(payload);
    console.log('server event triggered...', eventName, payload);
    return;
  }

  const baseUrl = resolveEventLoggerBaseUrl();
  if (!baseUrl || typeof window === 'undefined') return;

  const mergedPayload = {
    ...getCommonServerAttributes(),
    ...payload,
  };

  void fetch(`${baseUrl}/eventlogger/server/log`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: toFormBody(mergedPayload),
    credentials: 'include',
  }).catch(() => {
    // Fire-and-forget � legacy parity.
  });
}

function logFacebookClientEvent(
  eventName: string,
  eventAttributes: Record<string, unknown>,
  eventId: string,
): void {
  const payload = removeEmptyParams({
    ...getFacebookCommonAttributes(),
    ...eventAttributes,
    event_id: eventId,
  });

  //console.log(`Facebook Pixel Event '${eventName}' triggered:`, payload, { eventID: eventId });

  const track = () => {
    if (typeof window.fbq !== 'function') return false;
    try {
      window.fbq('track', eventName, payload, { eventID: eventId });
      console.log('client event triggered...', eventName, payload, { eventID: eventId });
      return true;
    } catch (error) {
      console.error(`Facebook Pixel track failed for '${eventName}':`, error);
      return false;
    }
  };

  if (track()) return;

  setTimeout(() => {
    if (!track()) {
      console.warn(`Facebook Pixel is still not loaded. Event '${eventName}' was skipped.`);
    }
  }, 1000);
}

export interface CommonFacebookEventsOptions {
  /** Skip server-side meta eventLogger POST */
  skipServer?: boolean;
}

/** Blocks Strict Mode remounts and loading-state re-runs from sending the same view twice. */
const recentFacebookEventKeys = new Map<string, number>();
const FACEBOOK_EVENT_DEDUPE_MS = 2000;

export function wasFacebookEventRecentlyFired(key: string, windowMs = FACEBOOK_EVENT_DEDUPE_MS): boolean {
  const now = Date.now();
  const previous = recentFacebookEventKeys.get(key);
  if (previous != null && now - previous < windowMs) return true;
  recentFacebookEventKeys.set(key, now);
  return false;
}

function pageViewDedupeKey(eventName: string, eventAttributes: Record<string, unknown>): string | null {
  if (eventName === 'PageView') {
    return `PageView:${window.location.pathname}`;
  }
  if (eventName === 'ViewContent') {
    const contentIds = toFacebookStringArray(eventAttributes.content_ids);
    const designId = eventAttributes.design_id != null ? String(eventAttributes.design_id) : '';
    const identity = contentIds[0] || designId;
    return identity ? `ViewContent:${identity}` : null;
  }
  return null;
}

/** Temporary rollout gate � only listed events fire. Remove to re-enable all events. */
const ENABLED_FACEBOOK_EVENTS = new Set([
  'PageView',
  'ViewContent',
  'AddToCart',
  'AddToWishlist',
  'CustomizeProduct',
  'FindLocation',
  'Search',
  'CompleteRegistration',
]);

const EXCLUDED_FACEBOOK_EVENT_HOSTS = [
  'oms.bluestone.com',
  'pos.bluestone.com',
  'warmup.bluestone.com',
];

/**
 * Meta event dispatcher: client pixel + server metaEvent.
 */
export function commonFacebookEventsUtil(
  eventName: string,
  eventAttributes: Record<string, unknown> = {},
  options?: CommonFacebookEventsOptions,
): void {
  if (typeof window === 'undefined') return;
  if (EXCLUDED_FACEBOOK_EVENT_HOSTS.includes(window.location.hostname)) return;

  if (!ENABLED_FACEBOOK_EVENTS.has(eventName)) return;

  const dedupeKey = pageViewDedupeKey(eventName, eventAttributes);
  if (dedupeKey && wasFacebookEventRecentlyFired(dedupeKey)) return;

  const eventId = generateEventId();
  const attributes = withNormalizedContentArrays({
    ...eventAttributes,
    event_id: eventId,
  });

  logFacebookClientEvent(eventName, attributes, eventId);

  if (!options?.skipServer) {
    logFacebookServerEvent(eventName, attributes);
  }
}

/** @deprecated Prefer commonFacebookEventsUtil for full client+server parity. */
export function trackFacebookEvent(
  eventName: string,
  customAttributes?: Record<string, unknown>,
): void {
  commonFacebookEventsUtil(eventName, customAttributes ?? {});
}

function isPlatinumProduct(metal: string, metalType: string, goldPurity: string): boolean {
  const haystack = `${metal} ${metalType} ${goldPurity}`.toLowerCase();
  return haystack.includes('platinum') || haystack.includes('950');
}

/** Gold karatage gets a KT suffix; platinum fineness is sent as-is (e.g. Platinum 95). */
export function formatFacebookPurity(
  goldPurity: string | null | undefined,
  metal: string,
  metalType: string,
): string {
  const purity = goldPurity?.trim() ?? '';
  if (isPlatinumProduct(metal, metalType, purity)) {
    if (!purity) return 'Platinum 95';
    return purity.replace(/kt$/i, '').trim();
  }
  if (!purity) return '';
  if (/kt$/i.test(purity)) return purity;
  return `${purity}KT`;
}

function isPresetSolitaireProduct(
  product: ProductDetail,
  vm: PdpEffectiveProductVm,
): boolean {
  return vm.presetSolitairePrice != null || product.presetSolitairePrice != null;
}

function buildFacebookPresetSolitaireAttributes(
  product: ProductDetail,
  vm: PdpEffectiveProductVm,
): Record<string, unknown> {
  const solitaireCarat = vm.solitaireCarat ?? product.solitaireCarat;

  return removeEmptyParams({
    solitaire_type: 'preset',
    solitaire_id: product.solitaireId,
    solitaire_shape: product.solitaireShape,
    solitaire_carat: solitaireCarat != null ? String(solitaireCarat) : null,
    solitaire_colour: product.solitaireColor,
    solitaire_clarity: product.solitaireClarity,
    solitaire_cut_grade: product.solitaireCutGrade,
  });
}

function resolveDefaultSizeDisplay(product: ProductDetail): string {
  const sizeKey = product.size;
  if (!sizeKey) return '';

  const sizes = parseSizeList(product.customizationOptions);
  const match = sizes.find(
    (option) =>
      String(option.key) === String(sizeKey) ||
      Number(option.key) === Number(sizeKey),
  );
  return match?.value ?? sizeKey;
}

/** PDP-specific Meta attributes (legacy getFacebookPDPAttr parity). */
export function buildFacebookPdpAttributes(
  product: ProductDetail,
  vm: PdpEffectiveProductVm,
  selectedSizeDisplay?: string,
): Record<string, unknown> {
  const solitaireCarat = vm.solitaireCarat ?? product.solitaireCarat;
  const diamondColorClarity = product.solitaireClarity ?? vm.diamondStoneType ?? '';

  const attributes: Record<string, unknown> = {
    content_ids: [String(product.designId)],
    content_type: 'product',
    content_names: product.designName ? [product.designName] : [],
    content_category: product.category ?? '',
    contents: [{ id: product.designId, quantity: 1 }],
    value: formatViewContentPrice(vm.discountedPrice),
    currency: 'INR',
    design_id: String(product.designId),
    variant_id: String(vm.activeCustomizationId),
    collection_name: product.collectionName ?? '',
    metal_type: vm.metalType ?? '',
    metal_colour: vm.metal ?? '',
    purity: formatFacebookPurity(vm.goldPurity, vm.metal, vm.metalType),
    stone_type: solitaireCarat ? 'Solitaire' : 'Diamond',
    diamond_quality: diamondColorClarity,
    size: selectedSizeDisplay?.trim()
      ? selectedSizeDisplay
      : resolveDefaultSizeDisplay(product),
  };

  if (isPresetSolitaireProduct(product, vm)) {
    Object.assign(attributes, buildFacebookPresetSolitaireAttributes(product, vm));
  }

  return attributes;
}
